import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Clock, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { useBusiness } from '../../contexts/BusinessContext';
import { calculateEndAcrossBusinessHours } from '../../lib/businessHoursSchedule';

interface CreateScheduleEditorProps {
  dropOffDate: string;
  dropOffTime: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  serviceDuration?: number;
  onDateTimeChange: (type: 'dropOff' | 'start' | 'end', date: string, time: string) => void;
  disabled?: boolean;
  hideDropOff?: boolean;
}

export default function CreateScheduleEditor({
  dropOffDate,
  dropOffTime,
  startDate,
  startTime,
  endDate,
  endTime,
  serviceDuration = 0,
  onDateTimeChange,
  disabled = false,
  hideDropOff = false
}: CreateScheduleEditorProps) {
  const { selectedBusiness } = useBusiness();
  const [showDateTimePicker, setShowDateTimePicker] = useState<'dropOff' | 'start' | 'end' | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [tempDate, setTempDate] = useState('');
  const [tempTime, setTempTime] = useState('');
  const timePickerRef = useRef<HTMLDivElement>(null);

  // Get the default opening time for a given day
  const getDefaultOpeningTime = (date: Date) => {
    if (!selectedBusiness?.business_hours) return '09:00';

    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayOfWeek = days[date.getDay()];
    const businessHours = selectedBusiness.business_hours;
    const daySchedule = businessHours[dayOfWeek as keyof typeof businessHours];

    if (daySchedule && daySchedule.enabled && !daySchedule.closed) {
      return daySchedule.open;
    }

    return '09:00'; // Default to 9am if no business hours set
  };

  // Scroll to selected time when picker opens
  React.useEffect(() => {
    if (showDateTimePicker) {
      setTimeout(() => {
        if (timePickerRef.current) {
          const selectedButton = timePickerRef.current.querySelector('[data-selected="true"]');
          if (selectedButton) {
            selectedButton.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }
      }, 100);
    }
  }, [showDateTimePicker]);

  // Generate time options in 30 minute increments
  const timeOptions = Array.from({ length: 48 }, (_, i) => {
    const hour = Math.floor(i / 2);
    const minute = i % 2 === 0 ? '00' : '30';
    const ampm = hour >= 12 ? 'pm' : 'am';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return {
      value: `${hour.toString().padStart(2, '0')}:${minute}`,
      label: `${displayHour}:${minute} ${ampm}`
    };
  });

  // Format date and time for display
  const formatDateTime = (date: string, time: string) => {
    if (!date || !time) return 'Select date and time';
    try {
      const dateObj = new Date(`${date}T${time}`);
      if (isNaN(dateObj.getTime())) {
        return 'Select date and time';
      }
      return dateObj.toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      });
    } catch (err) {
      console.error('Error formatting date/time:', err);
      return 'Select date and time';
    }
  };

  // Generate calendar days
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Adjust starting day to Monday (0 = Monday, 6 = Sunday)
    let startingDay = firstDay.getDay() - 1;
    if (startingDay === -1) startingDay = 6; // If Sunday, set to 6
    
    const days = [];
    
    // Add previous month's days
    for (let i = 0; i < startingDay; i++) {
      const prevDate = new Date(year, month, -startingDay + i + 1);
      days.push({
        date: prevDate,
        isCurrentMonth: false
      });
    }
    
    // Add current month's days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Add next month's days
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    
    return days;
  };

  // Handle date selection
  const handleDateClick = (date: Date) => {
    if (!showDateTimePicker) return;
    
    const formattedDate = date.getFullYear() + '-' + 
      String(date.getMonth() + 1).padStart(2, '0') + '-' + 
      String(date.getDate()).padStart(2, '0');

    setTempDate(formattedDate);
  };

  // Handle time selection
  const handleTimeClick = (time: string) => {
    if (!showDateTimePicker) return;
    setTempTime(time);
  };

  // Get selected date for current field
  const getSelectedDate = (type: 'dropOff' | 'start' | 'end'): string => {
    if (showDateTimePicker) {
      return tempDate;
    }
    return type === 'dropOff' ? dropOffDate :
           type === 'start' ? startDate :
           type === 'end' ? endDate : '';
  };

  // Get selected time for current field
  const getSelectedTime = (type: 'dropOff' | 'start' | 'end'): string => {
    if (showDateTimePicker) {
      return tempTime;
    }
    return type === 'dropOff' ? dropOffTime :
           type === 'start' ? startTime :
           type === 'end' ? endTime : '';
  };

  // Calculate end date/time based on business hours (shared with the AI
  // assistant and the schedule picker so all surfaces stretch identically)
  const calculateEndDateTime = (startDate: string, startTime: string, durationHours: number): { date: string, time: string } =>
    calculateEndAcrossBusinessHours(selectedBusiness?.business_hours, startDate, startTime, durationHours);

  // Handle setting the selected date/time
  const handleSetDateTime = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!showDateTimePicker) return;

    // Set the date/time for the selected field
    onDateTimeChange(showDateTimePicker, tempDate, tempTime);

    // If setting drop-off time, automatically set start time and end time
    if (showDateTimePicker === 'dropOff') {
      // Set start time to match drop-off time
      onDateTimeChange('start', tempDate, tempTime);
      
      // Calculate and set end time if we have a duration
      if (serviceDuration > 0) {
        const durationHours = serviceDuration / 60; // Convert minutes to hours
        const { date: endDate, time: endTime } = calculateEndDateTime(tempDate, tempTime, durationHours);
        onDateTimeChange('end', endDate, endTime);
      }
    }

    // If manually setting start time and we have a duration, recalculate end time
    else if (showDateTimePicker === 'start' && serviceDuration > 0) {
      const durationHours = serviceDuration / 60; // Convert minutes to hours
      const { date: endDate, time: endTime } = calculateEndDateTime(tempDate, tempTime, durationHours);
      onDateTimeChange('end', endDate, endTime);
    }

    setShowDateTimePicker(null);
  };

  // Handle canceling date/time selection
  const handleCancelDateTime = () => {
    setShowDateTimePicker(null);
  };

  // Initialize temp values when opening picker
  const handleOpenPicker = (type: 'dropOff' | 'start' | 'end') => {
    const currentDate = type === 'dropOff' ? dropOffDate :
                       type === 'start' ? startDate :
                       endDate;
    const currentTime = type === 'dropOff' ? dropOffTime :
                       type === 'start' ? startTime :
                       endTime;

    if (currentDate) {
      setTempDate(currentDate);
      setTempTime(currentTime || getDefaultOpeningTime(new Date(`${currentDate}T00:00:00`)));
      const parsedDate = new Date(`${currentDate}T00:00:00`);
      if (!isNaN(parsedDate.getTime())) {
        setSelectedMonth(new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1));
      }
    } else {
      const today = new Date();
      setTempDate(format(today, 'yyyy-MM-dd'));
      setTempTime(getDefaultOpeningTime(today));
      setSelectedMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    }

    setShowDateTimePicker(type);
  };

  // Check if a time is within business hours
  const isWithinBusinessHours = (time: string, dateStr: string | Date): boolean => {
    if (!selectedBusiness?.business_hours || !dateStr) {
      console.log('No business hours or date:', { businessHours: selectedBusiness?.business_hours, dateStr });
      return true;
    }

    try {
      // Create date object while preserving the local date
      const date = typeof dateStr === 'string' 
        ? new Date(dateStr + 'T00:00:00') 
        : new Date(dateStr.getFullYear(), dateStr.getMonth(), dateStr.getDate());
      
      if (isNaN(date.getTime())) {
        console.log('Invalid date:', dateStr);
        return true;
      }
      
      // Days array must match JavaScript's getDay() index (0 = Sunday, 1 = Monday, etc.)
      const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
      const dayIndex = date.getDay(); // 0-6, starting with Sunday
      const dayOfWeek = days[dayIndex];
      const businessHours = selectedBusiness.business_hours;
      
      console.log('Date debugging:', {
        inputDate: dateStr,
        parsedDate: date.toISOString(),
        localDate: date.toLocaleString(),
        dayIndex,
        dayOfWeek,
        dayName: date.toLocaleString('en-US', { weekday: 'long' })
      });

      const daySchedule = businessHours[dayOfWeek as keyof typeof businessHours];
      console.log('Business hours check:', {
        dayOfWeek,
        daySchedule,
        enabled: daySchedule?.enabled,
        closed: daySchedule?.closed,
        open: daySchedule?.open,
        close: daySchedule?.close
      });

      // First check if we have a schedule for this day
      if (!daySchedule) {
        console.log('No schedule found for day:', dayOfWeek);
        return false;
      }

      // Check if the day is enabled and not closed
      if (!daySchedule.enabled || daySchedule.closed) {
        console.log('Day is not available:', { 
          dayOfWeek, 
          enabled: daySchedule.enabled, 
          closed: daySchedule.closed 
        });
        return false;
      }

      // Finally check if the time is within business hours
      const timeToMinutes = (timeStr: string): number => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 60 + minutes;
      };

      const timeMinutes = timeToMinutes(time);
      const openMinutes = timeToMinutes(daySchedule.open);
      const closeMinutes = timeToMinutes(daySchedule.close);

      console.log('Time comparison:', {
        dayOfWeek,
        time,
        timeMinutes,
        openTime: daySchedule.open,
        openMinutes,
        closeTime: daySchedule.close,
        closeMinutes,
        isWithin: timeMinutes >= openMinutes && timeMinutes <= closeMinutes
      });

      return timeMinutes >= openMinutes && timeMinutes <= closeMinutes;
    } catch (error) {
      console.error('Error checking business hours:', error);
      return true;
    }
  };

  return (
    <div className="relative" style={{ pointerEvents: 'auto' }}>
      <div className="space-y-4" style={{ pointerEvents: 'auto' }}>
        {/* Drop-off Time */}
        {!hideDropOff && (
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Drop-off
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleOpenPicker('dropOff');
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                disabled={disabled}
                className="w-full h-10 px-3 text-left text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {formatDateTime(dropOffDate, dropOffTime)}
              </button>
              <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        )}

        {/* Start Time */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Start
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={(e) => {
                console.log('Start button clicked');
                e.preventDefault();
                e.stopPropagation();
                handleOpenPicker('start');
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              disabled={disabled}
              className="w-full h-10 px-3 text-left text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              style={{ pointerEvents: disabled ? 'none' : 'auto' }}
            >
              {formatDateTime(startDate, startTime)}
            </button>
            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* End Time */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            End
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleOpenPicker('end');
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              disabled={disabled}
              className="w-full h-10 px-3 text-left text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {formatDateTime(endDate, endTime)}
            </button>
            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Date Time Picker Popup - Rendered via Portal */}
      {showDateTimePicker && createPortal(
        <div 
          className="fixed inset-0 overflow-y-auto"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowDateTimePicker(null);
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          style={{
            zIndex: 100300,
            touchAction: 'manipulation',
            WebkitTouchCallout: 'none',
            WebkitUserSelect: 'none',
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
          }}
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
          <div className="flex min-h-full items-start justify-end p-4 sm:p-6" style={{ paddingTop: '15vh', paddingRight: '2rem' }}>
            <div 
              className="relative bg-white rounded-lg shadow-xl w-[500px] border border-gray-200"
              style={{ 
                maxHeight: '75vh',
                height: 'auto'
              }}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <div className="p-6">
                {/* Header */}
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Select Date & Time</h3>
                  <div className="text-sm text-gray-600">Choose your preferred date and time</div>
                </div>

                {/* Calendar and Time Picker Container */}
                <div className="flex flex-col space-y-3">
                  {/* Calendar */}
                  <div className="w-full">
                    <div className="flex items-center justify-between mb-3">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1));
                        }}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <ChevronLeft className="h-4 w-4 text-gray-600" />
                      </button>
                      <div className="text-base font-semibold text-gray-900">
                        {selectedMonth.toLocaleString('en-US', { month: 'long', year: 'numeric' })}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1));
                        }}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <ChevronRight className="h-4 w-4 text-gray-600" />
                      </button>
                    </div>

                    <div className="grid grid-cols-7 gap-1">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                        <div key={day} className="text-center text-xs font-bold text-gray-600 py-1">
                          {day}
                        </div>
                      ))}
                      {getDaysInMonth(selectedMonth).map((day, index) => {
                        const dateString = day.date.getFullYear() + '-' + 
                          String(day.date.getMonth() + 1).padStart(2, '0') + '-' + 
                          String(day.date.getDate()).padStart(2, '0');
                        const isSelected = dateString === getSelectedDate(showDateTimePicker);
                        
                        return (
                          <button
                            type="button"
                            key={`${dateString}-${index}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDateClick(day.date);
                            }}
                            className={`
                              text-sm rounded-lg transition-colors
                              w-8 h-8 flex items-center justify-center font-medium
                              ${!day.isCurrentMonth ? 'text-gray-400 hover:bg-gray-50' : 'text-gray-900 hover:bg-gray-100'}
                              ${isSelected ? 'bg-blue-500 text-white hover:bg-blue-600' : ''}
                            `}
                            style={{ minHeight: '32px', minWidth: '32px' }}
                          >
                            {day.date.getDate()}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Time Picker */}
                  <div>
                    <div className="text-sm font-bold text-gray-900 mb-2">Select Time</div>
                    <div 
                      ref={timePickerRef}
                      className="grid grid-cols-4 gap-1 max-h-[160px] overflow-y-auto"
                      style={{ WebkitOverflowScrolling: 'touch' }}
                    >
                      {timeOptions.map((time) => {
                        const isSelected = time.value === getSelectedTime(showDateTimePicker);
                        const withinHours = tempDate ? isWithinBusinessHours(time.value, tempDate) : true;
                        
                        return (
                          <button
                            type="button"
                            key={time.value}
                            data-selected={isSelected}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleTimeClick(time.value);
                            }}
                            className={`
                              px-3 py-2 text-xs rounded-lg transition-colors
                              font-medium border
                              ${isSelected 
                                ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' 
                                : withinHours 
                                  ? 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50 hover:border-gray-400' 
                                  : 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
                              }
                            `}
                            style={{ minHeight: '36px' }}
                          >
                            {time.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-4 border-t border-gray-200 pt-4 flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCancelDateTime();
                    }}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSetDateTime(e);
                    }}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Set Date & Time
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}